/**
 * /rules/reviews — cross-type review queue (Detection Rules v2).
 *
 * Adds a type filter to the toolbar. Approvers can act inline; clicking a
 * row will route to the per-type detail page that Wave 2 agents land.
 */

import { useMemo, useState } from "react";
import { CheckCircle2, ShieldCheck, XCircle } from "lucide-react";
import { Navigate } from "@tanstack/react-router";
import { toast } from "sonner";

import { ApiError } from "~/lib/api";
import { useCurrentUser } from "~/lib/auth";
import {
    RULE_TYPES,
    type RuleType,
    useApprove,
    useBaseRules,
    useReject,
    useReviewQueue,
    useTypeSeverityApprovers,
} from "~/lib/rules";
import { formatAbsolute, formatRelative } from "~/lib/time";

import { TableSkeleton } from "~/components/TableSkeleton";

export function RuleReviewQueuePage(): React.ReactElement {
    const me = useCurrentUser();
    const roles = me.data?.roles ?? [];
    const canView = roles.includes("admin") || roles.includes("operator") || roles.includes("rule_approver");
    const canApprove = roles.includes("admin") || roles.includes("rule_approver");

    const [typeFilter, setTypeFilter] = useState<RuleType | "">("");

    const reviewsQ = useReviewQueue({ status: "pending", type: typeFilter });
    const rulesQ = useBaseRules({}, 0, 200);
    const approversQ = useTypeSeverityApprovers();

    const rulesById = useMemo(() => {
        const m = new Map<string, import("~/lib/rules").Rule>();
        for (const r of rulesQ.data?.rules ?? []) m.set(r.id, r);
        return m;
    }, [rulesQ.data?.rules]);

    const neededFor = (type: RuleType, severity: import("~/lib/rules").RuleSeverity): number => {
        const rows = approversQ.data ?? [];
        return rows.find((r) => r.type === type && r.severity === severity)?.approversCount ?? 1;
    };

    if (me.data && !canView) return <Navigate to="/" />;

    return (
        <section className="stack" style={{ padding: "var(--space-6)", gap: "var(--space-5)" }}>
            <header className="cluster" style={{ justifyContent: "space-between", alignItems: "flex-end" }}>
                <div className="stack-xs">
                    <h1 className="type-h1">Review queue</h1>
                    <p className="type-body-muted">Pending reviews across all detection rule types.</p>
                </div>
                <label className="stack-xs">
                    <span className="text-12 text-text-muted">Type</span>
                    <select
                        className="input"
                        value={typeFilter}
                        onChange={(e) => setTypeFilter(e.target.value as RuleType | "")}
                    >
                        <option value="">All types</option>
                        {RULE_TYPES.map((t) => (
                            <option key={t.value} value={t.value}>
                                {t.label}
                            </option>
                        ))}
                    </select>
                </label>
            </header>

            {reviewsQ.isLoading ? (
                <TableSkeleton rows={6} />
            ) : (reviewsQ.data ?? []).length === 0 ? (
                <div className="card-surface" style={{ padding: "var(--space-5)", textAlign: "center" }}>
                    <ShieldCheck size={32} className="text-text-muted" />
                    <p className="type-body-muted" style={{ marginTop: "var(--space-3)" }}>
                        No pending reviews{typeFilter ? ` for ${typeFilter}` : ""}.
                    </p>
                </div>
            ) : (
                <table className="data-table">
                    <thead>
                        <tr>
                            <th>Rule</th>
                            <th>Type</th>
                            <th>Version</th>
                            <th>Requested</th>
                            <th>Needs</th>
                            {canApprove && <th style={{ textAlign: "right" }}>Actions</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {(reviewsQ.data ?? []).map((rv) => {
                            const rule = rulesById.get(rv.ruleId);
                            const needed = rule ? neededFor(rv.ruleType, rule.severity) : 1;
                            return (
                                <tr key={rv.id}>
                                    <td>
                                        <span className="type-mono-sm">{rule?.slug ?? rv.ruleId}</span>
                                        <span className="text-13" style={{ marginLeft: "var(--space-2)" }}>
                                            {rule?.name ?? "—"}
                                        </span>
                                    </td>
                                    <td>
                                        <span className="chip chip--subtle">{rv.ruleType}</span>
                                    </td>
                                    <td>v{rv.version}</td>
                                    <td title={formatAbsolute(rv.requestedAt)}>{formatRelative(rv.requestedAt)}</td>
                                    <td>{needed}</td>
                                    {canApprove && (
                                        <td style={{ textAlign: "right" }}>
                                            <ReviewActions reviewId={rv.id} />
                                        </td>
                                    )}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            )}
        </section>
    );
}

function ReviewActions({ reviewId }: { reviewId: string }): React.ReactElement {
    const approve = useApprove(reviewId);
    const reject = useReject(reviewId);
    async function onApprove(): Promise<void> {
        try {
            await approve.mutateAsync(undefined);
            toast.success("Approved");
        } catch (err) {
            toast.error(err instanceof ApiError ? err.message : "Approve failed");
        }
    }
    async function onReject(): Promise<void> {
        try {
            await reject.mutateAsync(undefined);
            toast.success("Rejected");
        } catch (err) {
            toast.error(err instanceof ApiError ? err.message : "Reject failed");
        }
    }
    return (
        <div className="cluster-xs" style={{ justifyContent: "flex-end" }}>
            <button type="button" className="btn btn--primary btn--sm" onClick={onApprove} disabled={approve.isPending}>
                <CheckCircle2 size={12} /> Approve
            </button>
            <button type="button" className="btn btn--ghost btn--sm" onClick={onReject} disabled={reject.isPending}>
                <XCircle size={12} /> Reject
            </button>
        </div>
    );
}
